import { useState } from 'react'

import type { Page } from '@models/Page'
import type { Query, Sort } from '@models/Query'
import type { SearchResults } from '@models/SearchResults'

import useLocalStorage from './useLocalStorage'

type Props<T, F> = {
  initialState: {
    query: Query<F>
    data: SearchResults<T> | T[] | Record<string, T[]>
  }
  query: Query<F>
  storeQuery: (query: Partial<Query<F>>) => void
  queryKey: string
}

export const useQueryWithStorage = <T, F extends Record<string, any>>({
  initialState,
  query,
  storeQuery,
  queryKey,
}: Props<T, F>) => {
  const { item: storedFilters } = useLocalStorage(
    queryKey,
    query.filters,
    initialState.query.filters,
  )

  const [localFilters, setLocalFilters] = useState(storedFilters)

  const onChangeFilters = (filters: F) => {
    setLocalFilters(filters)
    storeQuery({
      ...initialState.query,
      sort: query.sort,
      filters,
    })
  }

  const onRemoveSort = (filters: F) => {
    setLocalFilters(filters)
    storeQuery({ filters, sort: {}, page: initialState.query.page })
  }

  const onRestoreSort = (filters: F, sort: typeof query.sort) => {
    setLocalFilters(filters)
    storeQuery({ filters, sort })
  }

  const onChangeSort = (sort: Sort) => {
    storeQuery({ sort })
  }
  const onChangePage = (page: Page) => {
    storeQuery({ page })
  }

  const onReset = () => {
    setLocalFilters(initialState.query.filters)
    storeQuery({
      ...initialState.query,
    })
  }

  return {
    onChangeFilters,
    onChangeSort,
    onChangePage,
    onReset,
    onRemoveSort,
    onRestoreSort,
    query: {
      ...query,
      filters: localFilters,
    },
  }
}
